
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import {
  getFiltersDataFromQuery,
  joinQueryValues,
} from "@/helpers/router/prepareFiltersQuery";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import UiButton from "@/components/button/ui-button.vue";
import CustomRequestModal from "@/components/requests/CustomRequestModalNew.vue";
import {
  Company,
  Project,
  Team,
  Team_Bool_Exp,
  Query_RootTeamArgs,
  User,
  Manager_Contractor,
} from "@/generated/graphql";
import GroupCard from "@/components/teams/GroupCardNew.vue";
import GroupModal from "@/components/teams/GroupModalNew.vue";
import getPaginationOffset from "@/helpers/pagination/getPaginationOffset";
import PaginationConfig from "@/entities/PaginationConfig";
import { getEmptyFiltersResultMessage } from "@/helpers/lists/emptyListMessages";
import paginationConfig from "@/data/paginationConfig";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import CardPagination from "@/components/card/PaginationNew.vue";
import Roles from "@/entities/Roles";
import Spinner from '@/components/common/Spinner.vue';
import { cloneDeep, isEqual } from "lodash-es";
import {
  getPaginationDataFromQuery,
  getPaginationQuery,
} from "@/helpers/router/preparePaginationQuery";
import GroupsFiltersQuery from "@/entities/filters/GroupsFiltersQuery";
import GroupCollectionFilters from "@/components/teams/GroupCollectionFiltersNew.vue";
import ProjectCustomRequest from "@/components/projects/ProjectCustomRequestNew.vue";
import GET_PROJECT_GROUPS from "@/apollo/teams/GetProjectGroups.gql";
import PostingStatus from "@/entities/PostingStatus";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import groupsFiltersTemplate from "@/data/filtersTemplates/groupsFiltersTemplate";
import EmptyListImageRequests from "@/components/icons/emptyListImages/EmptyListImageGroups.vue";

interface GroupsQueryVariables {
  limit: number;
  offset: number;
  where: Team_Bool_Exp;
}

@Component({
  components: {
    CardPagination,
    EmptyListPlaceholder,
    EmptyListImageRequests,
    GroupCollectionFilters,
    GroupModal,
    PlusIcon,
    UiButton,
    CustomRequestModal,
    GroupCard,
    LoadingOverlay,
    Spinner,
    ProjectCustomRequest,
  },
  apollo: {
    groups: {
      query: GET_PROJECT_GROUPS,
      variables() {
        return this.groupFilter;
      },
      update(data) {
        this.paginationConfig.total = data.total.aggregate.count;
        this.initialGroup = data.team;
        const currentSessionValue = sessionStorage.getItem("isGroups");

        let currentValue = currentSessionValue ? JSON.parse(currentSessionValue) : [];
        if (Array.isArray(currentValue) && currentValue.length === 0) {
          sessionStorage.setItem("isGroups", JSON.stringify(data.team));
        }

        this.groups = data.team;
        return data.team;
      },
    },
  },
})
export default class Groups extends Vue {
  @Prop({ type: Object, required: true }) readonly project!: Project;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Object, required: true }) readonly company!: Company;

  groups: Team[] | null = null;
  initialGroup: Team[] | null = null;
  selectedGroup: Team | null = null;
  paginationConfig: PaginationConfig = { ...paginationConfig };
  modalShown: boolean = false;

  get hasData(): boolean {
    return Boolean(this.groups);
  }

  groupFilters: GroupsFiltersQuery = cloneDeep(groupsFiltersTemplate);

  toggle(group: Team): void {
    this.selectedGroup = group || null;
    this.modalShown = true;
  }
  get filtersEmpty(): boolean {
    return isEqual(this.groupFilters, groupsFiltersTemplate);
  }

  beforeUnmount() {
    sessionStorage.removeItem("isGroups");
  }

  get emptyListPlaceholder(): string {
    const entitiesName = "groups";

    if (this.filtersEmpty) {
      return `Start creating groups`;
    }

    return getEmptyFiltersResultMessage(entitiesName);
  }

  get groupFilter(): Query_RootTeamArgs {
    const { page, limit } = this.paginationConfig;
    const nextOffset = page * limit - limit;
    const { title, locations, managers, contractors, skills, postings } =
      this.groupFilters;

    const filterConditions: Team_Bool_Exp[] = [];

    if (title) {
      filterConditions.push({
        name: { _ilike: `%${title}%` },
      });
    }

    if (locations.length) {
      filterConditions.push({
        location: { _in: locations },
      });
    }

    if (contractors.length) {
      filterConditions.push({
        contractors: {
          contractor: {
            id: { _in: contractors },
          },
        },
      });
    }

    if (managers.length) {
      filterConditions.push({
        managers: {
          manager: {
            id: { _in: managers },
          },
        },
      });
    }

    if (postings.length) {
      filterConditions.push({
        postings: {
          posting: {
            job_title: { _in: postings },
          },
        },
      });
    }

    if (skills.length) {
      filterConditions.push({
        skills: {
          skill_id: { _in: skills },
        },
      });
    }

    return {
      offset: nextOffset,
      limit: limit,
      where: {
        _and: filterConditions,
        projects: {
          project_id: {
            _eq: this.project.id,
          },
        },
      },
    };
  }

  get queryPropsToWatch(): string {
    return JSON.stringify([this.groupFilters, this.paginationConfig]);
  }

  get queryParams(): {
    filters: GroupsFiltersQuery;
    pagination: PaginationConfig;
  } {
    const query = this.$route.query;
    const { posting_status: postingStatus, ...queryParams } = query;

    const queryData = {
      postingStatus,
      ...queryParams,
    };

    const filters = getFiltersDataFromQuery<GroupsFiltersQuery>(
      groupsFiltersTemplate,
      queryData
    );
    const pagination = {
      ...this.paginationConfig,
      ...getPaginationDataFromQuery(query),
    };

    return {
      filters,
      pagination,
    };
  }
  get isChosenPageEmpty(): boolean {
    const collectionEmpty = this.groups?.length === 0;
    const notFirstPage = this.paginationConfig.page !== 1;

    return !this.loading && collectionEmpty && notFirstPage;
  }

  @Watch("queryPropsToWatch")
  updateQuery(): void {
    if (this.isChosenPageEmpty) {
      return this.showFirstPage();
    }

    const paginationQuery = getPaginationQuery(this.paginationConfig);

    const { postingStatus, ...filtersData } = this.groupFilters;

    const query = joinQueryValues({
      ...paginationQuery,
      ...filtersData,
      posting_status: postingStatus,
    });

    if (isEqual(query, this.$route.query)) {
      return;
    }

    this.$router.push({ query }).catch(() => {});
  }

  @Watch("$route.query", { immediate: true })
  onQueryChange(): void {
    const { filters, pagination } = this.queryParams;

    this.groupFilters = filters;
    this.paginationConfig = pagination;
  }

  created() {
    if (!!this.company) {
      this.$apollo.queries.groups.skip = false;
    }
  }

  showFirstPage(): void {
    this.paginationConfig = {
      ...this.paginationConfig,
      page: paginationConfig.page,
    };
  }

  updateFilters(filters: GroupsFiltersQuery): void {
    const dataEqual = isEqual(filters, this.groupFilters);

    if (dataEqual) {
      return;
    }

    this.groupFilters = filters;
  }

  resetFilters(): void {
    if (isEqual(this.groupFilters, groupsFiltersTemplate)) {
      return;
    }

    this.groupFilters = cloneDeep(groupsFiltersTemplate);
    this.paginationConfig = { ...paginationConfig };
  }

  get isEmptyList(): boolean {
    const group = sessionStorage.getItem("isGroups");
    const isGroupExist = JSON.parse(group);
    if (isGroupExist && isGroupExist.length > 0) {
      return false;
    } else {
      return true;
    }
  }

  get isData(): boolean {
    if (!this.groups) {
      return false;
    }

    return this.groups.length > 0;
  }

  get currentManagerId(): undefined | string {
    return this.currentUser.role == Roles.MANAGER
      ? this.currentUser.manager?.id
      : undefined;
  }

  get managerAccessContractorsIds(): string[] {
    if (this.currentManagerId && this.currentUser.manager?.contractors) {
      return this.currentUser.manager.contractors.map(
        (c: Manager_Contractor) => c.contractor_id
      );
    }
    return [];
  }

  get loading(): boolean {
    return this.$apollo.queries.groups.loading;
  }

  get isAdmin(): boolean {
    return this.currentUser.role === Roles.ADMIN;
  }

  handleRefetch(): void {
    location.reload(); 
  }
}
